// src/views/auth/SignIn  (replace your existing sign-in file with this one — same import paths)
//
// --- UPDATE (v2 · sign-in redesign) -------------------------------------------------------
//   • New FlowSense MOW+ branded layout: brand panel over the existing illustration with what the
//     platform does and who it is for (managers, supervisors, helpdesk, field teams).
//   • "Create an Account" REMOVED — accounts are created by an administrator or manager in
//     User Management (the public sign-up is no longer offered).
//   • Non-working "Sign in with Google (Coming Soon)" button REMOVED.
//   • Caps Lock warning on the password field · show/hide is now a proper button.
//   • If the server marks the account "must_change_password" (temporary password from a reset),
//     the user is told and the flag is saved for the app (sessionStorage flowsense_must_change_pw).
//   UNCHANGED: loginRequest (POST /auth/login, email or username, in-flight de-dup), validation,
//   5-attempt / 5-minute lockout in sessionStorage, remember-me, storeAuthToken / storeUserInfo,
//   redirect to /admin/dashboard.
// --- END UPDATE ---------------------------------------------------------------------------
//
// --- UPDATE (v3 · forgot / reset password) -------------------------------------------------
//   • "Forgot password?" now passes what was typed (?identifier=) to /auth/forgot-password.
//   • After a reset the page opens with ?reset=success → green "Password changed" banner; the
//     query string is cleaned from the address bar.
//   • Locked-out users get a "Reset your password" link in the lockout alert.
//   Nothing else in the sign-in flow changed.
// --- END UPDATE ---------------------------------------------------------------------------

import React, { useState, useEffect, useRef, useCallback } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom"; // --- UPDATE: useLocation
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
  useColorModeValue,
  VStack,
  Alert,
  AlertIcon,
  Tooltip,
  HStack,
  FormErrorMessage,
  SimpleGrid,
  Badge,
} from "@chakra-ui/react";
import { MdOutlineRemoveRedEye, MdSecurity, MdVerifiedUser, MdPersonOutline, MdLockOutline, MdKeyboardCapslock, MdAutoAwesome, MdWaterDrop, MdEngineering, MdSupportAgent, MdInsights } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import { FaShieldAlt, FaLock, FaUserCheck } from "react-icons/fa";
import illustration from "../../assets/img/auth/auth.png";
import { config } from "../../config";
import { storeAuthToken, storeUserInfo } from "../../utils/auth";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const MAX_ATTEMPTS = 5;
const LOCKOUT_DURATION_S = 300; // 5 minutes
const NOTIFICATION_TTL_MS = 5000;
const REDIRECT_DELAY_MS = 1500;

// ---------------------------------------------------------------------------
// Standalone auth service — import from ../../services/auth in production
// ---------------------------------------------------------------------------
let _inflightRequest = null; // module-level deduplication guard

async function loginRequest(identifier, password, signal) {
  // Prevent concurrent identical requests (e.g. accidental double-click race)
  if (_inflightRequest) {
    return _inflightRequest;
  }

  const promise = (async () => {
    const payload = identifier.includes("@")
      ? { email: identifier, password }
      : { username: identifier, password };

    const response = await fetch(`${config.apiUrl}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal,
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.message || "Login failed");
    return data;
  })();

  _inflightRequest = promise;

  try {
    return await promise;
  } finally {
    _inflightRequest = null;
  }
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------
function validateForm(identifier, password) {
  const errors = {};
  if (!identifier.trim()) {
    errors.identifier = "Email or username is required";
  }
  if (!password) {
    errors.password = "Password is required";
  } else if (password.length < 6) {
    errors.password = "Password must be at least 6 characters";
  }
  return errors;
}

// ---------------------------------------------------------------------------
// Notification helper (inline — move to context/provider in larger apps)
// ---------------------------------------------------------------------------
function useNotification() {
  const [notification, setNotification] = useState(null);
  const timerRef = useRef(null);

  const show = useCallback((title, description, status) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setNotification({ title, description, status });
    timerRef.current = setTimeout(() => setNotification(null), NOTIFICATION_TTL_MS);
  }, []);

  const dismiss = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setNotification(null);
  }, []);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return { notification, show, dismiss };
}

// ---------------------------------------------------------------------------
// Lockout hook — persists lockout state in sessionStorage so a page refresh
// doesn't reset the counter (but a new tab/session does).
// ---------------------------------------------------------------------------
function useLockout() {
  const [attempts, setAttempts] = useState(() => {
    try { return parseInt(sessionStorage.getItem("loginAttempts") || "0", 10); } catch { return 0; }
  });
  const [lockedUntil, setLockedUntil] = useState(() => {
    try { return parseInt(sessionStorage.getItem("lockedUntil") || "0", 10); } catch { return 0; }
  });
  const [now, setNow] = useState(Date.now);

  const isLocked = lockedUntil > now;
  const lockTimeRemaining = isLocked ? Math.ceil((lockedUntil - now) / 1000) : 0;

  useEffect(() => {
    if (!isLocked) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isLocked]);

  const recordFailure = useCallback(() => {
    const next = attempts + 1;
    setAttempts(next);
    try { sessionStorage.setItem("loginAttempts", String(next)); } catch {}

    if (next >= MAX_ATTEMPTS) {
      const until = Date.now() + LOCKOUT_DURATION_S * 1000;
      setLockedUntil(until);
      try { sessionStorage.setItem("lockedUntil", String(until)); } catch {}
    }
    return next;
  }, [attempts]);

  const resetLockout = useCallback(() => {
    setAttempts(0);
    setLockedUntil(0);
    try {
      sessionStorage.removeItem("loginAttempts");
      sessionStorage.removeItem("lockedUntil");
    } catch {}
  }, []);

  return { isLocked, lockTimeRemaining, attempts, recordFailure, resetLockout };
}

// --- UPDATE: brand panel content ---
const HIGHLIGHTS = [
  { icon: MdEngineering, title: "Work orders & dispatch", text: "Crews, SLAs and escalations in one place." },
  { icon: MdSupportAgent, title: "Helpdesk & customers", text: "Calls, tickets and customer field visits." },
  { icon: MdInsights, title: "Assets, roads & network", text: "Inspections, segment health and risk." },
  { icon: MdAutoAwesome, title: "FlowSense AI", text: "Predictions, detections and explanations." },
];
const AUDIENCE = ["Managers", "Supervisors", "Office staff", "Helpdesk", "Inspectors", "Technicians", "Contractors"];
// --- END UPDATE ---

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
function SignIn() {
  // --- Theme ---
  const textColor          = useColorModeValue("gray.800", "white");
  const textColorSecondary = useColorModeValue("gray.500", "gray.400");
  const textColorBrand     = useColorModeValue("blue.600", "blue.300");
  const authBg             = useColorModeValue("white", "gray.900");
  const inputBg            = useColorModeValue("gray.50", "gray.800");
  const inputFocusBg       = useColorModeValue("white", "gray.800");
  const borderColor        = useColorModeValue("gray.200", "gray.600");
  const securityBadgeBg    = useColorModeValue("blue.50", "rgba(66,153,225,0.12)");
  const securityBadgeText  = useColorModeValue("blue.700", "blue.200");
  const featuresBoxBg      = useColorModeValue("gray.50", "gray.800");
  const iconMuted          = useColorModeValue("gray.400", "gray.500");
  // --- UPDATE: reset-success banner colours ---
  const resetOkBg          = useColorModeValue("green.50", "rgba(72,187,120,0.12)");
  const resetOkText        = useColorModeValue("green.800", "green.200");
  // --- END UPDATE ---

  // --- State ---
  const [show, setShow]               = useState(false);
  const [identifier, setIdentifier]   = useState("");
  const [password, setPassword]       = useState("");
  const [rememberMe, setRememberMe]   = useState(false);
  const [errors, setErrors]           = useState({});
  const [capsLock, setCapsLock]       = useState(false); // --- UPDATE

  const isSubmittingRef = useRef(false);
  const [isLoading, setIsLoading]     = useState(false);
  const abortRef = useRef(null);

  // --- Hooks ---
  const navigate = useNavigate();
  const { notification, show: showNotification, dismiss } = useNotification();
  const { isLocked, lockTimeRemaining, attempts, recordFailure, resetLockout } = useLockout();

  // --- UPDATE: ?reset=success after the password-reset page ---
  const location = useLocation();
  const [resetDone, setResetDone] = useState(() => {
    try { return new URLSearchParams(location.search).get("reset") === "success"; } catch { return false; }
  });
  useEffect(() => {
    if (!resetDone) return;
    try { window.history.replaceState(window.history.state, "", location.pathname); } catch { /* ignore */ }
    resetLockout(); // a fresh password clears the local lockout counter
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const forgotHref = identifier.trim()
    ? `/auth/forgot-password?identifier=${encodeURIComponent(identifier.trim())}`
    : "/auth/forgot-password";
  // --- END UPDATE ---

  // --- Persist remembered user ---
  useEffect(() => {
    try {
      const remembered = localStorage.getItem("rememberMe");
      const lastUser   = localStorage.getItem("lastUser");
      if (remembered === "true" && lastUser) {
        setIdentifier(lastUser);
        setRememberMe(true);
      }
    } catch { /* localStorage may be unavailable */ }
  }, []);

  useEffect(() => () => { abortRef.current?.abort(); }, []);

  // --- Handlers ---
  const handleIdentifierChange = useCallback((e) => {
    setIdentifier(e.target.value);
    setErrors((prev) => ({ ...prev, identifier: undefined }));
  }, []);

  const handlePasswordChange = useCallback((e) => {
    setPassword(e.target.value);
    setErrors((prev) => ({ ...prev, password: undefined }));
  }, []);

  // --- UPDATE: Caps Lock detection ---
  const handlePasswordKey = useCallback((e) => {
    if (typeof e.getModifierState === "function") setCapsLock(e.getModifierState("CapsLock"));
  }, []);
  // --- END UPDATE ---

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (isLocked) {
      showNotification(
        "Account Temporarily Locked",
        `Please wait ${lockTimeRemaining}s before trying again.`,
        "error"
      );
      return;
    }

    if (isSubmittingRef.current) return;

    const formErrors = validateForm(identifier, password);
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    isSubmittingRef.current = true;
    setIsLoading(true);

    abortRef.current?.abort();
    abortRef.current = new AbortController();

    try {
      const data = await loginRequest(identifier, password, abortRef.current.signal);

      resetLockout();

      if (data.token)  storeAuthToken(data.token);
      if (data.user)   storeUserInfo(data.user);

      try {
        if (rememberMe) {
          localStorage.setItem("rememberMe", "true");
          localStorage.setItem("lastUser", identifier);
        } else {
          localStorage.removeItem("rememberMe");
          localStorage.removeItem("lastUser");
        }
      } catch { /* ignore storage errors */ }

      // --- UPDATE: temporary password notice ---
      const mustChange = !!(data.must_change_password || data.user?.must_change_password);
      try {
        if (mustChange) sessionStorage.setItem("flowsense_must_change_pw", "1");
        else sessionStorage.removeItem("flowsense_must_change_pw");
      } catch { /* ignore */ }
      // --- END UPDATE ---

      showNotification(
        "Welcome back!",
        mustChange
          ? `Signed in as ${data.user?.username ?? identifier}. You are using a temporary password — change it in User Management → "Change my password".`
          : `Signed in as ${data.user?.username ?? identifier}. Redirecting…`,
        mustChange ? "warning" : "success"
      );

      setTimeout(() => navigate("/admin/dashboard"), mustChange ? REDIRECT_DELAY_MS * 2 : REDIRECT_DELAY_MS);

    } catch (err) {
      if (err.name === "AbortError") return;

      const nextAttempts = recordFailure();

      if (nextAttempts >= MAX_ATTEMPTS) {
        showNotification(
          "Account Locked",
          `Too many failed attempts. Locked for ${LOCKOUT_DURATION_S / 60} minutes.`,
          "error"
        );
      } else {
        showNotification(
          "Sign In Failed",
          `${err.message || "Invalid credentials."}  ${MAX_ATTEMPTS - nextAttempts} attempt(s) remaining.`,
          "error"
        );
      }
    } finally {
      isSubmittingRef.current = false;
      setIsLoading(false);
    }
  }, [
    isLocked, lockTimeRemaining,
    identifier, password, rememberMe,
    navigate, showNotification, recordFailure, resetLockout,
  ]);

  const remainingAttempts = MAX_ATTEMPTS - attempts;
  const disabled = isLocked || isLoading;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Flex position="relative" minH="100vh" w="100%" bg={authBg}>

      {/* ── Notification ─────────────────────────────────────────────────── */}
      {notification && (
        <Box position="fixed" top="20px" right="20px" left={{ base: "20px", md: "auto" }} zIndex={9999} maxW={{ md: "420px" }} role="alert" aria-live="assertive">
          <Alert status={notification.status} borderRadius="xl" boxShadow="xl" onClick={dismiss} cursor="pointer">
            <AlertIcon />
            <Box>
              <Text fontWeight="bold" fontSize="sm">{notification.title}</Text>
              <Text fontSize="xs">{notification.description}</Text>
            </Box>
          </Alert>
        </Box>
      )}

      {/* ── Left: Form ───────────────────────────────────────────────────── */}
      <Flex
        direction="column"
        w={{ base: "100%", md: "50%", lg: "45%" }}
        px={{ base: 6, md: 12, xl: 20 }}
        py={{ base: 10, md: 16 }}
        justifyContent="center"
        alignItems="center"
        overflowY="auto"
      >
        <Box w="100%" maxW="420px">

          {/* Brand */}
          <HStack spacing={3} mb={8}>
            <Flex w="44px" h="44px" borderRadius="xl" bgGradient="linear(to-br, blue.500, purple.600)" align="center" justify="center" boxShadow="md">
              <Icon as={MdWaterDrop} color="white" boxSize={6} />
            </Flex>
            <Box>
              <Text fontWeight="900" fontSize="lg" color={textColor} lineHeight="1.1">FlowSense <Text as="span" color={textColorBrand}>MOW+</Text></Text>
              <Text fontSize="xs" color={textColorSecondary}>Utility operations platform</Text>
            </Box>
          </HStack>

          {/* Header */}
          <VStack spacing={1} align="flex-start" mb={6}>
            <Heading color={textColor} fontSize={{ base: "2xl", md: "3xl" }} fontWeight="800">Welcome back</Heading>
            <Text fontSize="md" color={textColorSecondary}>Sign in with your FlowSense account to continue.</Text>
          </VStack>

          {/* --- UPDATE: password reset confirmation --- */}
          {resetDone && (
            <HStack data-testid="reset-success-banner" spacing={3} p={3} mb={4} bg={resetOkBg} borderRadius="xl" align="flex-start" role="status">
              <Icon as={MdVerifiedUser} color="green.400" mt={0.5} />
              <Box flex="1">
                <Text fontSize="sm" fontWeight="bold" color={resetOkText}>Password changed</Text>
                <Text fontSize="xs" color={resetOkText}>Sign in with your new password. Use it in the FlowSense Tech app too.</Text>
              </Box>
              <Button size="xs" variant="ghost" onClick={() => setResetDone(false)} aria-label="Dismiss">✕</Button>
            </HStack>
          )}
          {/* --- END UPDATE --- */}

          {/* Lockout alert */}
          {isLocked && (
            <Alert status="error" borderRadius="lg" mb={4} role="alert">
              <AlertIcon />
              <Box>
                <Text fontWeight="bold" fontSize="sm">Account Temporarily Locked</Text>
                <Text fontSize="xs">Try again in {lockTimeRemaining}s</Text>
                {/* --- UPDATE --- */}
                <Text as={NavLink} to={forgotHref} fontSize="xs" fontWeight="bold" textDecoration="underline">Forgot your password? Reset it by email</Text>
                {/* --- END UPDATE --- */}
              </Box>
            </Alert>
          )}

          {/* Attempts warning */}
          {attempts > 0 && attempts < MAX_ATTEMPTS && !isLocked && (
            <Alert status="warning" borderRadius="lg" mb={4} role="status">
              <AlertIcon />
              <Text fontSize="sm">
                {attempts} failed attempt{attempts !== 1 ? "s" : ""}.{" "}
                {remainingAttempts} remaining before lockout.
              </Text>
            </Alert>
          )}

          {/* ── Form ─────────────────────────────────────────────────────── */}
          <Box as="form" onSubmit={handleSubmit} noValidate aria-label="Sign in form">
            <VStack spacing={5} align="stretch">

              {/* Email / Username */}
              <FormControl isRequired isInvalid={!!errors.identifier}>
                <FormLabel fontSize="sm" fontWeight="semibold" color={textColor} htmlFor="identifier">Email or username</FormLabel>
                <InputGroup size="lg">
                  <InputLeftElement pointerEvents="none"><Icon as={MdPersonOutline} color={iconMuted} /></InputLeftElement>
                  <Input
                    id="identifier"
                    name="identifier"
                    type="text"
                    placeholder="you@company.com or username"
                    borderRadius="xl"
                    value={identifier}
                    onChange={handleIdentifierChange}
                    bg={inputBg}
                    _focus={{ bg: inputFocusBg }}
                    borderColor={errors.identifier ? "red.500" : borderColor}
                    isDisabled={disabled}
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    autoFocus={!identifier}
                    aria-describedby={errors.identifier ? "identifier-error" : undefined}
                  />
                </InputGroup>
                <FormErrorMessage id="identifier-error">{errors.identifier}</FormErrorMessage>
              </FormControl>

              {/* Password */}
              <FormControl isRequired isInvalid={!!errors.password}>
                <Flex justify="space-between" align="baseline">
                  <FormLabel fontSize="sm" fontWeight="semibold" color={textColor} htmlFor="password">Password</FormLabel>
                  <Text as={NavLink} to={forgotHref /* --- UPDATE: was "/auth/forgot-password" */} fontSize="sm" color={textColorBrand} fontWeight="medium" _hover={{ textDecoration: "underline" }}>
                    Forgot password?
                  </Text>
                </Flex>
                <InputGroup size="lg">
                  <InputLeftElement pointerEvents="none"><Icon as={MdLockOutline} color={iconMuted} /></InputLeftElement>
                  <Input
                    id="password"
                    name="password"
                    type={show ? "text" : "password"}
                    placeholder="••••••••"
                    borderRadius="xl"
                    value={password}
                    onChange={handlePasswordChange}
                    onKeyDown={handlePasswordKey}
                    onKeyUp={handlePasswordKey}
                    onBlur={() => setCapsLock(false)}
                    bg={inputBg}
                    _focus={{ bg: inputFocusBg }}
                    borderColor={errors.password ? "red.500" : borderColor}
                    isDisabled={disabled}
                    autoComplete="current-password"
                    autoFocus={!!identifier}
                    aria-describedby={errors.password ? "password-error" : capsLock ? "caps-warning" : undefined}
                  />
                  <InputRightElement>
                    <IconButton
                      size="sm"
                      variant="ghost"
                      icon={<Icon as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye} />}
                      onClick={() => setShow((s) => !s)}
                      color="gray.400"
                      aria-label={show ? "Hide password" : "Show password"}
                      type="button"
                    />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage id="password-error">{errors.password}</FormErrorMessage>
                {capsLock && (
                  <HStack id="caps-warning" spacing={1} mt={2} color="orange.400" role="status">
                    <Icon as={MdKeyboardCapslock} />
                    <Text fontSize="xs" fontWeight="semibold">Caps Lock is on</Text>
                  </HStack>
                )}
              </FormControl>

              {/* Remember me */}
              <Checkbox colorScheme="blue" size="md" isChecked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} isDisabled={disabled}>
                <Text fontSize="sm">Keep me signed in on this device</Text>
              </Checkbox>

              {/* Submit */}
              <Button
                w="100%"
                h="52px"
                type="submit"
                bgGradient="linear(to-r, blue.500, purple.600)"
                color="white"
                _hover={{ bgGradient: "linear(to-r, blue.600, purple.700)", boxShadow: "lg" }}
                _active={{ bgGradient: "linear(to-r, blue.700, purple.800)" }}
                borderRadius="xl"
                isLoading={isLoading}
                loadingText="Signing in…"
                isDisabled={disabled}
                fontSize="md"
                fontWeight="bold"
                leftIcon={!isLoading ? <Icon as={FaLock} /> : undefined}
                aria-busy={isLoading}
              >
                {isLocked ? `Locked (${lockTimeRemaining}s)` : "Sign in securely"}
              </Button>
            </VStack>
          </Box>

          {/* --- UPDATE: no public sign-up --- */}
          <HStack mt={6} spacing={3} p={3} bg={securityBadgeBg} borderRadius="xl" align="flex-start">
            <Icon as={MdSecurity} color={securityBadgeText} mt={0.5} />
            <Text fontSize="xs" color={securityBadgeText}>
              Accounts are created by your FlowSense administrator or manager. Need access, or locked out? Contact your administrator or the helpdesk.
            </Text>
          </HStack>
          {/* --- END UPDATE --- */}

          {/* Security feature badges */}
          <HStack mt={6} p={3} spacing={4} bg={featuresBoxBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} justify="center" wrap="wrap">
            <Tooltip label="Encrypted connection (TLS)">
              <HStack spacing={1}><Icon as={FaShieldAlt} color="green.500" boxSize={3} /><Text fontSize="xs">Encrypted</Text></HStack>
            </Tooltip>
            <Tooltip label="Role-based access: you only see what your role allows">
              <HStack spacing={1}><Icon as={MdVerifiedUser} color="blue.500" boxSize={3} /><Text fontSize="xs">Role-based access</Text></HStack>
            </Tooltip>
            <Tooltip label={`Brute-force protection: locked for ${LOCKOUT_DURATION_S / 60} minutes after ${MAX_ATTEMPTS} failed attempts`}>
              <HStack spacing={1}><Icon as={FaUserCheck} color="purple.500" boxSize={3} /><Text fontSize="xs">Lockout protection</Text></HStack>
            </Tooltip>
          </HStack>

          <Text mt={6} fontSize="xs" color={textColorSecondary} textAlign="center">
            © {new Date().getFullYear()} FlowSense MOW+ · Authorised users only
          </Text>
        </Box>
      </Flex>

      {/* ── Right: Brand panel over the illustration ─────────────────────── */}
      <Flex
        w={{ base: "0%", md: "50%", lg: "55%" }}
        display={{ base: "none", md: "flex" }}
        position="relative"
        bgImage={`url(${illustration})`}
        bgSize="cover"
        bgPosition="center"
        borderBottomLeftRadius={{ lg: "120px", xl: "200px" }}
        overflow="hidden"
      >
        <Box position="absolute" inset={0} bgGradient="linear(to-br, rgba(30,64,175,0.92), rgba(88,28,135,0.88))" />
        <Flex position="relative" direction="column" justify="center" px={{ md: 10, xl: 20 }} py={16} color="white" w="100%">
          <Badge alignSelf="flex-start" bg="whiteAlpha.200" color="white" px={3} py={1} borderRadius="full" mb={5} display="flex" alignItems="center" gap={1}>
            <Icon as={MdAutoAwesome} /> Powered by FlowSense AI
          </Badge>
          <Heading fontSize={{ md: "3xl", xl: "4xl" }} fontWeight="900" lineHeight="1.15" mb={4} maxW="520px">
            One platform for the whole operation.
          </Heading>
          <Text fontSize="md" color="whiteAlpha.800" mb={10} maxW="480px">
            Work orders, helpdesk, field teams, assets and roads — with AI that predicts, detects and explains.
          </Text>
          <SimpleGrid columns={{ md: 1, lg: 2 }} spacing={4} maxW="560px" mb={10}>
            {HIGHLIGHTS.map((h) => (
              <HStack key={h.title} align="flex-start" spacing={3} p={4} bg="whiteAlpha.100" borderRadius="xl" backdropFilter="blur(6px)" borderWidth="1px" borderColor="whiteAlpha.200">
                <Flex w="36px" h="36px" borderRadius="lg" bg="whiteAlpha.200" align="center" justify="center" flexShrink={0}><Icon as={h.icon} boxSize={5} /></Flex>
                <Box>
                  <Text fontWeight="800" fontSize="sm">{h.title}</Text>
                  <Text fontSize="xs" color="whiteAlpha.800">{h.text}</Text>
                </Box>
              </HStack>
            ))}
          </SimpleGrid>
          <Text fontSize="xs" textTransform="uppercase" fontWeight="800" letterSpacing="wider" color="whiteAlpha.700" mb={2}>Built for every role</Text>
          <Flex wrap="wrap" gap={2} maxW="560px">
            {AUDIENCE.map((a) => <Badge key={a} bg="whiteAlpha.200" color="white" px={2.5} py={1} borderRadius="full" textTransform="none" fontWeight="600">{a}</Badge>)}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}

export default SignIn;
